import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { Plus, Package, Loader2, ArrowRight, Bell, Truck, Heart, RotateCcw, User, MapPin, Lock, Trash2, Save, ShieldCheck, Undo2, X, Gift, Quote, Star, CheckCircle2, Hourglass, LayoutGrid, FileText, LifeBuoy, LogOut, Search, Pill, Check, PawPrint, Stethoscope, Phone, Send } from "lucide-react";

// Pending estimates (quoted but not yet paid) — shown alongside orders so the dashboard
// reflects an order the moment the quote/secure-link email is sent.
const EST_STATUS = {
  quoted:       { label: "Upload prescription", tone: "amber", cta: "Upload prescription", needsYou: true,
                  hint: "Send us the prescription from your vet and a licensed pharmacy technician reviews it, usually within 24 hours." },
  rx_in_review: { label: "In review", tone: "blue", cta: "View quote", needsYou: false,
                  hint: "A licensed pharmacy technician is checking your prescription. Payment opens on the same link once it clears." },
  rx_approved:  { label: "Ready to pay", tone: "green", cta: "Pay now", needsYou: true,
                  hint: "Your prescription has been approved. Complete payment and the pharmacy begins filling it." },
  rx_rejected:  { label: "Action needed", tone: "red", cta: "Fix and resubmit", needsYou: true,
                  hint: "There was a problem with the prescription you sent. Open the link to see what is needed." },
}

const STATUS_CONFIG = {
  pending_payment: { label: "Pending payment", tone: "amber" },
  pending_vet_review: { label: "Quality check", tone: "blue" },
  under_vet_review: { label: "Quality check", tone: "blue" },
  under_review: { label: "Quality check", tone: "blue" },
  vet_approved: { label: "Approved", tone: "green" },
  cosigned: { label: "Approved", tone: "green" },
  rejected: { label: "Rejected", tone: "red" },
  sent_to_fulfillment: { label: "At the pharmacy", tone: "blue" },
  processing: { label: "Being prepared", tone: "blue" },
  being_prepared: { label: "Being prepared", tone: "blue" },
  shipped: { label: "Shipped", tone: "blue" },
  delivered: { label: "Delivered", tone: "green" },
  completed: { label: "Delivered", tone: "green" },
  cancelled: { label: "Cancelled", tone: "grey" },
  refunded: { label: "Refunded", tone: "grey" },
};

// Six-step journey shown in the hero. The value is the index of the step the order is ON.
const STEPS = ["Estimate", "Prescription", "Vet review", "Pharmacy fills", "Shipped", "Delivered"];
const STEP_AT = {
  pending_payment: 1,
  pending_vet_review: 2,
  under_vet_review: 2,
  under_review: 2,
  rejected: 2,
  vet_approved: 3,
  cosigned: 3,
  sent_to_fulfillment: 3,
  processing: 3,
  being_prepared: 3,
  shipped: 4,
  delivered: 5,
  completed: 5,
};

// The backend treats delivered and completed as the same terminal state
// (DELIVERED_STATUSES). Anything here is history, not something in motion.
const TERMINAL = ["delivered", "completed", "cancelled", "refunded"];

// A quote link is only valid for 48 hours. Past that the estimate endpoint 410s,
// so the dashboard must stop presenting it as something you can act on.
const EXPIRED_CFG = { label: "Expired", tone: "grey", cta: "Get a new quote", needsYou: false, expired: true };
const isExpired = (est) => {
  const t = Date.parse(est?.link_expires_at || "");
  return Number.isFinite(t) && t < Date.now();
};

const TONE = {
  blue: "text-[#0F62D6] bg-[#F3F7FE] border-[#CBDFF9]",
  green: "text-[#0E9F6E] bg-[#E6F6F0] border-[#BFE9D8]",
  amber: "text-[#B54708] bg-[#FEF4E6] border-[#F7DFC0]",
  red: "text-[#B42318] bg-[#FEF3F2] border-[#FECDC9]",
  grey: "text-[#667085] bg-[#F2F4F7] border-[#EAECF0]",
};

function Badge({ tone = "grey", children, dot = true }) {
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11.5px] font-bold whitespace-nowrap ${TONE[tone] || TONE.grey}`}>
      {dot && <i className="w-1.5 h-1.5 rounded-full bg-current not-italic" />} {children}
    </span>
  );
}

const NAV = [
  { id: "overview",      label: "Overview",          icon: LayoutGrid,  group: "Menu" },
  { id: "orders",        label: "Orders",            icon: Package,     group: "Menu" },
  { id: "prescriptions", label: "Prescriptions",     icon: FileText,    group: "Menu" },
  { id: "pets",          label: "My pets",           icon: PawPrint,    group: "Menu" },
  { id: "vetrefill",     label: "Ask my vet",        icon: Stethoscope, group: "Menu" },
  { id: "savings",       label: "Savings",           icon: Truck,       group: "Menu" },
  { id: "profile",       label: "Profile & address", icon: User,        group: "Account" },
  { id: "testimonial",   label: "Testimonial",       icon: Quote,       group: "Account" },
  { id: "security",      label: "Security",          icon: ShieldCheck, group: "Account" },
];

export default function CustomerDashboard({ mirror = null, readOnly = false }) {
  const auth = useAuth();
  const { checkAuth, logout } = auth;
  // Admin support renders this same component with a preloaded, read-only payload
  // (GET /admin/customers/{id}/dashboard) so there is only ever one dashboard.
  const isMirror = !!mirror;
  const user = isMirror ? (mirror.customer || null) : auth.user;
  const authLoading = isMirror ? false : auth.loading;
  const navigate = useNavigate();
  const blocked = () => {
    if (!readOnly) return false;
    toast.info("Read-only view. Actions are disabled here.");
    return true;
  };
  const [tab, setTab] = useState("overview");
  const [petFilter, setPetFilter] = useState(null);
  const [orders, setOrders] = useState([]);
  /* Refills we chase with the customer's own vet on their behalf. Separate from a normal
     reorder, because here there is no valid prescription left and only the vet can fix that. */
  const [refillReqs, setRefillReqs] = useState([]);
  const [rfOpen, setRfOpen] = useState(false);
  const [rfSaving, setRfSaving] = useState(false);
  const [rf, setRf] = useState({ order_id: "", pet_name: "", pet_type: "", medication_name: "",
                                 strength: "", dosage_form: "", pack_size: 0, packs: 1,
                                 quantity_label: "", clinic_name: "", clinic_email: "",
                                 clinic_phone: "", note: "" });
  const [pendingEstimates, setPendingEstimates] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  // Profile + address
  const [profile, setProfile] = useState({ first_name: "", last_name: "", phone: "", address: "", dob: "" });
  const [addressParts, setAddressParts] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);

  // Password
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [savingPassword, setSavingPassword] = useState(false);

  // Delete
  const [deleting, setDeleting] = useState(false);
  // Refill in progress (order_id)
  const [refilling, setRefilling] = useState(null);
  // Testimonial — one per account, ever. The tab is locked until the first delivery and the
  // server enforces both rules independently, so this state only drives what is displayed.
  const [testimonial, setTestimonial] = useState(null);
  const [tBody, setTBody] = useState("");
  const [tRating, setTRating] = useState(5);
  const [tPet, setTPet] = useState("");
  const [tConsent, setTConsent] = useState(true);
  const [tSubmitting, setTSubmitting] = useState(false);

  // Refund request modal
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundSubmitting, setRefundSubmitting] = useState(false);

  useEffect(() => {
    if (!isMirror && !authLoading && (!user || user === false)) navigate("/login");
    if (user && user !== false) {
      setProfile({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        dob: user.dob || "",
        address: user.address || "",
      });
      setAddressParts(user.address_parts || null);
    }
  }, [authLoading, user, navigate, isMirror]);

  useEffect(() => {
    if (isMirror) return;
    if (user && user !== false) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Returning from a refill checkout (?refilled=1&session_id=...): confirm the payment
  // directly (webhook-independent) so the refill registers, then refresh + clean the URL.
  useEffect(() => {
    if (isMirror) return;
    if (!user || user === false) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("refilled") !== "1") return;
    (async () => {
      const sid = params.get("session_id");
      let confirmed = !sid; // covered/sandbox refills return without a session_id and are already confirmed
      if (sid) {
        try {
          const { data } = await api.post("/orders/verify-refill", { session_id: sid });
          confirmed = !!data?.paid;
        } catch { /* fall through to pending message */ }
      }
      if (confirmed) toast.success("Refill confirmed. Your order is being prepared.");
      else toast.info("Payment received. Your refill is being confirmed and will appear below shortly.");
      window.history.replaceState({}, "", "/dashboard");
      fetchData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!isMirror) return;
    setOrders(mirror.orders || []);
    setPendingEstimates(mirror.pending_estimates || []);
    setRewards(mirror.rewards || null);
    setNotifications((mirror.notifications || []).filter(n => !n.is_read).slice(0, 5));
    setTestimonial(mirror.testimonial || null);
    setLoading(false);
  }, [isMirror, mirror]);

  const RF_STATUS = {
    pending_review: { label: "We are reviewing your request", tint: "bg-[#F3F7FE] border-[#E6EFFC] text-[#0F62D6]", pill: "bg-[#F3F7FE] text-[#0F62D6]" },
    not_sent:       { label: "On hold, we will be in touch", tint: "bg-[#F9FAFB] border-[#EAECF0] text-[#98A2B3]", pill: "bg-[#F2F4F7] text-[#667085]" },
    emailed:    { label: "Waiting on your vet", tint: "bg-[#FEF4E6] border-[#F7DFC0] text-[#B54708]", pill: "bg-[#FEF4E6] text-[#B54708]" },
    called:     { label: "We are calling them",  tint: "bg-[#FEF4E6] border-[#F7DFC0] text-[#B54708]", pill: "bg-[#FEF4E6] text-[#B54708]" },
    authorized: { label: "Authorized",           tint: "bg-[#E6F6F0] border-[#C5EADD] text-[#0E9F6E]", pill: "bg-[#E6F6F0] text-[#0E9F6E]" },
    declined:   { label: "Vet wants to see them", tint: "bg-[#FDECEC] border-[#F7C9C9] text-[#BC4749]", pill: "bg-[#FDECEC] text-[#BC4749]" },
    closed:     { label: "Closed",               tint: "bg-[#F9FAFB] border-[#EAECF0] text-[#98A2B3]", pill: "bg-[#F2F4F7] text-[#667085]" },
  };

  const pickRefillOrder = (e) => {
    const o = orders.find(x => x.order_id === e.target.value);
    setRf(p => ({
      ...p,
      order_id: e.target.value,
      medication_name: o?.medication?.name || "",
      strength: o?.medication?.dosage || "",
      dosage_form: o?.medication?.dosage_form || "",
      pack_size: Number(o?.medication?.pack_units || o?.medication?.pack_size || 0) || 0,
      packs: Number(o?.medication?.packs || 1) || 1,
      quantity_label: o?.medication?.pack_units
        ? `${o.medication.packs || 1} x ${o.medication.pack_units}`
        : (o?.medication?.quantity ? String(o.medication.quantity) : ""),
      pet_name: o?.pet?.name || "",
      pet_type: o?.pet?.species || "",
    }));
  };

  const submitRefillRequest = async (e) => {
    e.preventDefault();
    if (!rf.medication_name) { toast.error("Choose which medication you need refilled."); return; }
    setRfSaving(true);
    try {
      await api.post("/refill-requests", rf);
      toast.success("Request received. We check it over, send it to your vet, and email you as soon as they reply.");
      setRfOpen(false);
      setRf({ order_id: "", pet_name: "", pet_type: "", medication_name: "", strength: "",
              dosage_form: "", pack_size: 0, packs: 1,
              quantity_label: "", clinic_name: "", clinic_email: "", clinic_phone: "", note: "" });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRfSaving(false); }
  };

  const fetchData = async () => {
    try {
      const [ordersRes, estimatesRes, rewardsRes, notifsRes, testimonialRes, refillRes] = await Promise.all([
        api.get("/orders"),
        api.get("/my-pending-estimates").catch(() => ({ data: [] })),
        api.get("/my-rewards").catch(() => ({ data: null })),
        api.get("/notifications"),
        api.get("/my-testimonial").catch(() => ({ data: null })),
        api.get("/my-refill-requests").catch(() => ({ data: [] })),
      ]);
      setOrders(ordersRes.data);
      setPendingEstimates(estimatesRes.data || []);
      setRewards(rewardsRes.data || null);
      setTestimonial(testimonialRes.data || null);
      setRefillReqs(refillRes?.data || []);
      setNotifications(notifsRes.data.filter(n => !n.is_read).slice(0, 5));
    } catch {
      // Was silent: a failed fetch looked identical to "No active orders yet".
      toast.error("We couldn't load your orders just now. Please refresh the page.");
    } finally {
      setLoading(false);
    }
  };

  // Submitting is a one-way door. The server claims the slot atomically, so a 409 here means
  // it was already used — refetch rather than argue with it, so the tab shows the truth.
  const submitTestimonial = async (e) => {
    e.preventDefault();
    if (blocked()) return;
    const min = testimonial?.min_chars || 40;
    const body = tBody.trim();
    if (body.length < min) {
      toast.error(`Please write at least ${min} characters so it is useful to other pet owners.`);
      return;
    }
    setTSubmitting(true);
    try {
      const { data } = await api.post("/my-testimonial", {
        body,
        rating: tRating,
        pet_name: tPet.trim(),
        consent_publish: tConsent,
      });
      toast.success(data?.message || "Thank you. Your free delivery has been added to your account.");
      setTBody("");
      await fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
      if (err?.response?.status === 409 || err?.response?.status === 403) fetchData();
    } finally {
      setTSubmitting(false);
    }
  };

  const markRead = async (id) => {
    if (blocked()) return;
    await api.patch(`/notifications/${id}/read`);
    setNotifications(notifications.filter(n => n.notification_id !== id));
  };

  const reorder = (order) => {
    if (blocked()) return;
    const medName = order?.medication?.name;
    window.dispatchEvent(new CustomEvent("open-estimate-modal", medName ? { detail: { query: medName } } : undefined));
  };

  // One-click refill: reuses the original (still-valid) prescription, no re-upload and no
  // re-review. Charges medication + 50%-off shipping, then sends the customer to Stripe.
  const handleRefill = async (e, order) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (blocked()) return;
    setRefilling(order.order_id);
    try {
      const { data } = await api.post(`/orders/${order.order_id}/refill`, { origin_url: window.location.origin });
      if (data?.url) { window.location.href = data.url; return; }
      toast.success("Refill started.");
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefilling(null);
    }
  };

  // Refund request: customer explains why, support@pawlinx.com is emailed for human review.
  // No money moves automatically.
  const openRefund = (e, order) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (blocked()) return;
    setRefundReason("");
    setRefundOrder(order);
  };
  const submitRefund = async () => {
    if (blocked()) return;
    if (refundReason.trim().length < 5) { toast.error("Please describe the reason for your refund."); return; }
    setRefundSubmitting(true);
    try {
      const { data } = await api.post(`/orders/${refundOrder.order_id}/request-refund`, { reason: refundReason.trim() });
      toast.success(data?.message || "Refund request submitted for review.");
      setRefundOrder(null);
      fetchData();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRefundSubmitting(false);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (blocked()) return;
    setSavingProfile(true);
    try {
      await api.patch("/account", {
        first_name: profile.first_name,
        last_name: profile.last_name,
        phone: profile.phone,
        dob: profile.dob,
        address: profile.address,
        address_parts: addressParts,
      });
      await checkAuth();
      toast.success("Profile saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (blocked()) return;
    if (passwords.new_password !== passwords.confirm_password) { toast.error("Passwords do not match"); return; }
    if (passwords.new_password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSavingPassword(true);
    try {
      await api.post("/account/change-password", {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      setPasswords({ current_password: "", new_password: "", confirm_password: "" });
      toast.success("Password changed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingPassword(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (blocked()) return;
    if (!window.confirm("Delete your account? Your information will be kept for 30 days, then permanently removed. You can cancel any time within those 30 days by logging back in.")) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/account/request-deletion");
      toast.success(data?.message || "Your account is scheduled for deletion.");
      try { await logout(); } catch { /* ignore */ }
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDeleting(false);
    }
  };

  const openEstimate = () => { if (blocked()) return; window.dispatchEvent(new Event("open-estimate-modal")); };
  const newQuoteFor = (medName) => { if (blocked()) return; window.dispatchEvent(new CustomEvent("open-estimate-modal", medName ? { detail: { query: medName } } : undefined)); };
  const estCfg = (est) => {
    if (isExpired(est)) {
      const on = est?.link_expires_at ? new Date(est.link_expires_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;
      return { ...EXPIRED_CFG, hint: `This quote expired${on ? ` on ${on}` : ""}. Medication prices move, so we will price it again for you.` };
    }
    return EST_STATUS[est?.status] || { label: "In progress", tone: "grey", cta: "View quote", needsYou: false, hint: "We will email you as soon as there is an update." };
  };

  if (authLoading || loading) {
    return <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]"><Loader2 className="w-8 h-8 animate-spin text-[#0F62D6]" /></div>;
  }

  const activeOrders = orders.filter(o => !TERMINAL.includes(o.status));
  const pastOrders = orders.filter(o => TERMINAL.includes(o.status));
  const heroOrder = activeOrders[0] || null;
  const heroStep = heroOrder ? (STEP_AT[heroOrder.status] ?? 1) : 0;
  const initials = `${(user?.first_name || "").charAt(0)}${(user?.last_name || "").charAt(0)}`.toUpperCase() || "P";
  const liveEstimates = pendingEstimates.filter(e => !isExpired(e));
  const expiredEstimates = pendingEstimates.filter(isExpired);
  const actionEstimates = liveEstimates.filter(e => EST_STATUS[e.status]?.needsYou);

  // Pets are derived from what has actually been ordered or quoted - there is no
  // separate pet record to read, so this is the only truthful source.
  const petIndex = {};
  const addPet = (name, species, opts = {}) => {
    const n = (name || "").trim();
    if (!n) return;
    const k = n.toLowerCase();
    petIndex[k] = petIndex[k] || { name: n, species: species || "", active: 0, needsRx: false, meds: [] };
    if (species && !petIndex[k].species) petIndex[k].species = species;
    if (opts.active) petIndex[k].active += 1;
    if (opts.needsRx) petIndex[k].needsRx = true;
    if (opts.med && !petIndex[k].meds.some(m => m.label === opts.med.label && m.sub === opts.med.sub)) petIndex[k].meds.push(opts.med);
  };
  orders.forEach(o => addPet(o.pet?.name, o.pet?.species, {
    active: !TERMINAL.includes(o.status),
    med: { label: o.medication?.name, sub: `${STATUS_CONFIG[o.status]?.label || o.status} · $${Number(o.payment?.amount || 0).toFixed(2)}`, to: `/order/${o.order_id}` },
  }));
  pendingEstimates.forEach(e => addPet(e.pet_name, e.pet_type, {
    active: true,
    needsRx: !isExpired(e) && !!EST_STATUS[e.status]?.needsYou,
    med: { label: e.medication_name, sub: `${estCfg(e).label}${e.quote != null && !isExpired(e) ? ` · $${Number(e.quote).toFixed(2)}` : ""}`, to: isExpired(e) ? "/dashboard" : `/estimate/${e.link_token}` },
  }));
  const pets = Object.values(petIndex);

  // Hero: the paid order in flight if there is one, otherwise the quote that most
  // needs the customer, so the page is never headless.
  const EST_STEP = { quoted: 1, rx_rejected: 1, rx_in_review: 2, rx_approved: 2 };
  const heroEst = heroOrder ? null : (actionEstimates[0] || liveEstimates[0] || null);
  const heroEstCfg = heroEst ? estCfg(heroEst) : null;

  const matchesPet = (name) => !petFilter || (name || "").toLowerCase() === petFilter;
  const shownOrders = activeOrders.filter(o => matchesPet(o.pet?.name));
  const shownEstimates = pendingEstimates.filter(e => matchesPet(e.pet_name));

  const money = (n) => `$${Number(n || 0).toFixed(2)}`;
  const dateShort = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";

  /* ---------------- shared pieces ---------------- */

  const Tracker = ({ current }) => (
    <div className="flex mt-6">
      {STEPS.map((s, i) => (
        <div key={s} className="flex-1 relative text-center">
          {i < STEPS.length - 1 && (
            <span className={`absolute top-[8px] left-1/2 w-full h-[2px] ${i < current ? "bg-white" : "bg-white/25"}`} />
          )}
          <span className={`relative z-[2] mx-auto mb-2.5 flex items-center justify-center w-[18px] h-[18px] rounded-full ${i < current ? "bg-white" : i === current ? "bg-white ring-[5px] ring-white/25" : "bg-white/25"}`}>
            {i < current && <Check className="w-2.5 h-2.5 text-[#0F62D6]" strokeWidth={4} />}
          </span>
          <small className={`block text-[10.5px] sm:text-[11px] font-semibold leading-tight ${i <= current ? "text-white" : "text-white/60"}`}>{s}</small>
        </div>
      ))}
    </div>
  );

  const StatCard = ({ icon: Icon, tint, label, value, sub, valueClass = "" }) => (
    <div className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
      <div className="flex items-center gap-2.5">
        <span className={`w-[34px] h-[34px] rounded-[10px] flex items-center justify-center shrink-0 ${tint}`}><Icon className="w-4 h-4" /></span>
        <span className="text-[12.5px] font-semibold text-[#667085]">{label}</span>
      </div>
      <div className={`text-[25px] font-extrabold tracking-[-.03em] mt-3 leading-none ${valueClass}`}>{value}</div>
      <div className="text-[12px] text-[#667085] mt-2">{sub}</div>
    </div>
  );

  const navCount = (id) => {
    if (id === "orders") return activeOrders.length + liveEstimates.length;
    if (id === "prescriptions") return actionEstimates.length;
    return 0;
  };

  const NavItem = ({ n }) => {
    const c = navCount(n.id);
    return (
      <button
        onClick={() => { setTab(n.id); setPetFilter(null); }}
        data-testid={`tab-${n.id}`}
        className={`flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[14px] font-semibold transition-colors whitespace-nowrap w-full ${tab === n.id ? "bg-[#F3F7FE] text-[#0F62D6]" : "text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828]"}`}
      >
        <n.icon className="w-[18px] h-[18px] shrink-0" />
        {n.label}
        {c > 0 && (
          <span className={`ml-auto text-[11px] font-bold min-w-[20px] h-5 rounded-full flex items-center justify-center px-1.5 ${n.id === "prescriptions" ? "bg-[#FEF4E6] text-[#B54708]" : "bg-[#0F62D6] text-white"}`}>{c}</span>
        )}
      </button>
    );
  };

  const SideNav = ({ mobile = false }) => (
    mobile ? (
      <nav className="flex gap-1.5 overflow-x-auto pb-1">
        {NAV.map((n) => <div key={n.id} className="shrink-0"><NavItem n={n} /></div>)}
      </nav>
    ) : (
      <>
        <div className="text-[11px] font-bold tracking-[.09em] uppercase text-[#98A2B3] px-3 mb-2">Menu</div>
        <nav className="flex flex-col gap-0.5">{NAV.filter(n => n.group === "Menu").map(n => <NavItem key={n.id} n={n} />)}</nav>
        <div className="text-[11px] font-bold tracking-[.09em] uppercase text-[#98A2B3] px-3 mt-4 mb-2">Account</div>
        <nav className="flex flex-col gap-0.5">{NAV.filter(n => n.group === "Account").map(n => <NavItem key={n.id} n={n} />)}</nav>
        <Link to="/contact" className="flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] text-[14px] font-semibold text-[#475467] hover:bg-[#F2F4F7] hover:text-[#101828] transition-colors">
          <LifeBuoy className="w-[18px] h-[18px] shrink-0" /> Help centre
        </Link>
      </>
    )
  );

  /* ---------------- order card ---------------- */

  const OrderCard = ({ order }) => {
    const cfg = STATUS_CONFIG[order.status] || { label: order.status, tone: "grey" };
    const step = STEP_AT[order.status] ?? 1;
    const pct = Math.round(((step + 1) / STEPS.length) * 100);
    const hint = order.rx_exhausted
      ? "Prescription used. A new prescription from your vet is needed to order this again."
      : order.refill_eligible
      ? `Prescription valid${typeof order.rx_days_left === "number" ? ` · ${order.rx_days_left} days left` : ""}, no new upload needed`
      : order.refund_requested ? "Refund request under review" : "We email you at every step.";
    return (
      <div className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)] hover:border-[#CBDFF9] hover:shadow-[0_1px_3px_rgba(16,24,40,.06),0_8px_20px_-6px_rgba(16,24,40,.08)] transition-all mb-3" data-testid={`order-card-${order.order_id}`}>

        <div className="flex gap-4 items-start">
          <span className="w-[46px] h-[46px] rounded-xl shrink-0 flex items-center justify-center bg-[#F3F7FE] border border-[#E6EFFC] text-[#0F62D6]">
            <Pill className="w-[18px] h-[18px]" />
          </span>
          <Link to={`/order/${order.order_id}`} className="flex-1 min-w-0 pt-0.5">
            <h4 className="text-[15px] font-extrabold tracking-[-.012em] truncate">{order.medication?.name}</h4>
            <div className="text-[12.5px] text-[#667085] mt-1 truncate">
              {order.pet?.name}{order.pet?.species ? ` (${order.pet.species})` : ""} · {order.order_number} · {dateShort(order.created_at)}
            </div>
          </Link>
          <div className="shrink-0 text-right pt-0.5">
            <Badge tone={cfg.tone}>{cfg.label}</Badge>
            <div className="text-[16px] font-extrabold tracking-[-.02em] mt-2">{money(order.payment?.amount)}</div>
          </div>
        </div>

        <div className="mt-4">
          <div className="h-1.5 rounded-full bg-[#F2F4F7] overflow-hidden">
            <i className="block h-full rounded-full bg-gradient-to-r from-[#0F62D6] to-[#3B87F0]" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-[11.5px] text-[#667085] mt-2 font-medium">
            <span>{cfg.label}</span>
            <span>Step {step + 1} of {STEPS.length}</span>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[#F2F4F7] flex items-center gap-3 flex-wrap">
          <span className="text-[12.5px] text-[#667085]">{hint}</span>
          <div className="flex gap-2 ml-auto shrink-0">
            {order.refund_requested ? (
              <Badge tone="amber">Refund requested</Badge>
            ) : order.refundable ? (
              <button onClick={(e) => openRefund(e, order)} data-testid={`refund-${order.order_id}`} className="h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#FECDC9] hover:text-[#B42318] transition-colors">
                Request refund
              </button>
            ) : null}
            {order.refill_eligible ? (
              <button onClick={(e) => handleRefill(e, order)} disabled={refilling === order.order_id} data-testid={`refill-${order.order_id}`} className="h-9 px-3.5 rounded-[9px] bg-[#0F62D6] hover:bg-[#0A4CAB] text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60 transition-colors">
                {refilling === order.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Refill · 50% off shipping
              </button>
            ) : (
              <Link to={`/order/${order.order_id}`} className="h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6] inline-flex items-center transition-colors">
                Order details
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  };

  const EstimateCard = ({ est }) => {
    const cfg = estCfg(est);
    const dead = !!cfg.expired;
    return (
      <div data-testid={`pending-estimate-${est.estimate_id}`} className={`bg-white border rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)] transition-all mb-3 ${dead ? "border-[#EAECF0]" : cfg.needsYou ? "border-[#F7DFC0] hover:shadow-[0_1px_3px_rgba(16,24,40,.06),0_8px_20px_-6px_rgba(16,24,40,.08)]" : "border-[#EAECF0] hover:shadow-[0_1px_3px_rgba(16,24,40,.06),0_8px_20px_-6px_rgba(16,24,40,.08)]"}`}>

        <div className="flex gap-4 items-start">
          <span className={`w-[46px] h-[46px] rounded-xl shrink-0 flex items-center justify-center border ${dead ? "bg-[#F9FAFB] border-[#EAECF0] text-[#98A2B3]" : cfg.needsYou ? "bg-[#FEF4E6] border-[#F7DFC0] text-[#B54708]" : "bg-[#F3F7FE] border-[#E6EFFC] text-[#0F62D6]"}`}>
            <FileText className="w-[18px] h-[18px]" />
          </span>
          {dead ? (
            <div className="flex-1 min-w-0 pt-0.5">
              <h4 className="text-[15px] font-extrabold tracking-[-.012em] truncate text-[#667085]">{est.medication_name}</h4>
              <div className="text-[12.5px] text-[#98A2B3] mt-1 truncate capitalize">
                {est.pet_name ? `${est.pet_name}${est.pet_type ? ` (${est.pet_type})` : ""} · ` : ""}quoted {dateShort(est.created_at)}
              </div>
            </div>
          ) : (
            <Link to={`/estimate/${est.link_token}`} className="flex-1 min-w-0 pt-0.5">
              <h4 className="text-[15px] font-extrabold tracking-[-.012em] truncate">{est.medication_name}</h4>
              <div className="text-[12.5px] text-[#667085] mt-1 truncate capitalize">
                {est.pet_name ? `${est.pet_name}${est.pet_type ? ` (${est.pet_type})` : ""} · ` : ""}quote sent {dateShort(est.created_at)}
              </div>
            </Link>
          )}
          <div className="shrink-0 text-right pt-0.5">
            <Badge tone={cfg.tone}>{cfg.label}</Badge>
            <div className={`text-[16px] font-extrabold tracking-[-.02em] mt-2 ${dead ? "text-[#98A2B3] line-through" : ""}`}>
              {est.client_type === "insurance_referral" ? "Covered" : est.quote != null ? money(est.quote) : ""}
            </div>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-[#F2F4F7] flex items-center gap-3 flex-wrap">
          <span className="text-[12.5px] text-[#667085]">{cfg.hint}</span>
          {dead ? (
            <button onClick={() => newQuoteFor(est.medication_name)} data-testid={`requote-${est.estimate_id}`} className="ml-auto shrink-0 h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6] inline-flex items-center gap-2 transition-colors">
              {cfg.cta} <ArrowRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <Link to={`/estimate/${est.link_token}`} className={`ml-auto shrink-0 h-9 px-3.5 rounded-[9px] text-[13px] font-bold inline-flex items-center gap-2 transition-colors ${cfg.needsYou ? "bg-[#0F62D6] hover:bg-[#0A4CAB] text-white" : "border border-[#EAECF0] bg-white text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6]"}`}>
              {cfg.cta} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </div>
    );
  };

  /* ---------------- history table ---------------- */
  function HistoryTable({ rows }) {
    return (
      <div className="bg-white border border-[#EAECF0] rounded-2xl overflow-hidden shadow-[0_1px_2px_rgba(16,24,40,.05)]">
        <div className="hidden md:grid grid-cols-[1fr_170px_110px_130px] gap-4 px-5 py-3 bg-[#FCFCFD] border-b border-[#EAECF0] text-[11.5px] font-bold tracking-[.05em] uppercase text-[#667085]">
          <span>Medication</span><span>Status</span><span>Total</span><span className="text-right">Action</span>
        </div>
        {rows.map(order => {
          const cfg = STATUS_CONFIG[order.status] || { label: order.status, tone: "grey" };
          return (
            <div key={order.order_id} data-testid={`past-order-${order.order_id}`} className="grid grid-cols-1 md:grid-cols-[1fr_170px_110px_130px] gap-2 md:gap-4 px-5 py-4 items-center border-b border-[#F2F4F7] last:border-b-0 hover:bg-[#FCFDFF]">
              <Link to={`/order/${order.order_id}`} className="min-w-0">
                <div className="text-[14px] font-bold truncate">{order.medication?.name}</div>
                <div className="text-[12px] text-[#667085] mt-0.5 truncate">{order.pet?.name} · {order.order_number} · {dateShort(order.created_at)}</div>
              </Link>
              <div><Badge tone={cfg.tone}>{cfg.label}</Badge></div>
              <div className="text-[14px] font-bold">{money(order.payment?.amount)}</div>
              <div className="md:text-right flex md:justify-end gap-2">
                {order.refund_requested ? (
                  <Badge tone="amber">Refund requested</Badge>
                ) : order.refundable ? (
                  <button onClick={() => openRefund(null, order)} data-testid={`refund-${order.order_id}`} className="h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#FECDC9] hover:text-[#B42318]">Refund</button>
                ) : null}
                {order.refill_eligible ? (
                  <button onClick={() => handleRefill(null, order)} disabled={refilling === order.order_id} data-testid={`refill-${order.order_id}`} className="h-9 px-3.5 rounded-[9px] bg-[#0F62D6] hover:bg-[#0A4CAB] text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60">
                    {refilling === order.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Refill
                  </button>
                ) : (
                  <button onClick={() => reorder(order)} data-testid={`reorder-${order.order_id}`} className="h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6]">Reorder</button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ---------------- render ---------------- */

  return (
    <div className="min-h-screen flex bg-[#F7F8FA] font-sans text-[#101828]" data-testid="customer-dashboard">

      {/* sidebar */}
      <aside className="hidden lg:flex w-[258px] shrink-0 bg-white border-r border-[#EAECF0] p-5 flex-col sticky top-0 h-screen">
        <Link to="/" className="flex items-center px-2 pb-6">
          <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-8 w-auto select-none" draggable={false} />
        </Link>

        <SideNav />

        <div className="mt-auto rounded-[14px] p-4 bg-gradient-to-br from-[#0F62D6] to-[#0A3E8C] text-white shadow-[0_8px_20px_-6px_rgba(16,24,40,.2)]">
          <div className="text-[13.5px] font-bold">Refills ship at 50% off</div>
          <div className="text-[12px] text-white/70 mt-1.5 leading-relaxed">While your pet&apos;s prescription stays valid, every refill ships at half the standard rate.</div>
          <button onClick={openEstimate} className="mt-3 w-full h-[34px] rounded-lg bg-white text-[#0F62D6] text-[12.5px] font-bold">Get an estimate</button>
        </div>

        <div className="flex items-center gap-2.5 mt-4 pt-4 border-t border-[#EAECF0]">
          <span className="w-[34px] h-[34px] rounded-full bg-gradient-to-br from-[#3B87F0] to-[#0A4CAB] text-white flex items-center justify-center text-[12.5px] font-bold shrink-0">{initials}</span>
          <div className="min-w-0">
            <div className="text-[13px] font-bold leading-tight truncate capitalize">{user?.first_name} {user?.last_name}</div>
            <div className="text-[11.5px] text-[#667085] truncate">{user?.email}</div>
          </div>
          {!readOnly && (
            <button onClick={() => { logout(); navigate("/"); }} className="ml-auto text-[#667085] hover:text-[#0F62D6]" aria-label="Log out"><LogOut className="w-[18px] h-[18px]" /></button>
          )}
        </div>
      </aside>

      {/* main */}
      <div className="flex-1 min-w-0">

        <header className="sticky top-0 z-20 bg-white border-b border-[#EAECF0] px-4 sm:px-8 h-[70px] flex items-center gap-4">
          <Link to="/" className="lg:hidden flex items-center">
            <img src="/pawlinx-logo-nav.png" alt="PawLinx" className="h-7 w-auto select-none" draggable={false} />
          </Link>
          <div className="hidden md:flex flex-1 max-w-[400px] h-10 border border-[#EAECF0] rounded-[10px] items-center gap-2.5 px-3.5 bg-[#FCFCFD] text-[#667085]">
            <Search className="w-[15px] h-[15px]" />
            <input
              placeholder="Search your medications"
              onKeyDown={(e) => { if (e.key === "Enter" && e.currentTarget.value.trim()) window.dispatchEvent(new CustomEvent("open-estimate-modal", { detail: { query: e.currentTarget.value.trim() } })); }}
              className="bg-transparent border-none outline-none text-[13.5px] w-full text-[#101828]"
            />
          </div>
          <div className="ml-auto flex items-center gap-3">
            {notifications.length > 0 && (
              <button onClick={() => setTab("overview")} className="relative w-10 h-10 rounded-[10px] border border-[#EAECF0] bg-white flex items-center justify-center text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6]" aria-label="Notifications">
                <Bell className="w-[18px] h-[18px]" />
                <span className="absolute top-2 right-2.5 w-[7px] h-[7px] rounded-full bg-[#F04438] border-[1.5px] border-white" />
              </button>
            )}
            <button onClick={openEstimate} data-testid="new-order-btn" className="h-10 px-4 rounded-[10px] bg-[#0F62D6] hover:bg-[#0A4CAB] text-white text-[14px] font-bold inline-flex items-center gap-2 transition-colors">
              <Plus className="w-[15px] h-[15px]" /> <span className="hidden sm:inline">Get an estimate</span><span className="sm:hidden">Estimate</span>
            </button>
          </div>
        </header>

        {/* mobile nav */}
        <div className="lg:hidden bg-white border-b border-[#EAECF0] px-4 py-2.5">
          <SideNav mobile />
        </div>

        <div className="px-4 sm:px-8 py-6 pb-16 max-w-[1280px]">

          {/* ============ OVERVIEW ============ */}
          {tab === "overview" && (
            <>
              <div className="flex items-end justify-between gap-5 flex-wrap mb-5">
                <div>
                  <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em]">Welcome back, <span className="capitalize">{user?.first_name}</span></h1>
                  <p className="text-[14px] text-[#667085] mt-1.5">
                    {activeOrders.length + pendingEstimates.length > 0
                      ? `${activeOrders.length + liveEstimates.length} order${activeOrders.length + pendingEstimates.length === 1 ? "" : "s"} in motion${actionEstimates.length ? ` · ${actionEstimates.length} waiting on you` : ""}${expiredEstimates.length ? ` · ${expiredEstimates.length} quote${expiredEstimates.length === 1 ? "" : "s"} expired` : ""}`
                      : "Everything is up to date."}
                  </p>
                </div>

                {pets.length > 0 && (
                  <div className="flex gap-2.5 flex-wrap" data-testid="pet-chips">
                    {pets.map((pet) => {
                      const on = petFilter === pet.name.toLowerCase();
                      return (
                        <button
                          key={pet.name}
                          onClick={() => setPetFilter(on ? null : pet.name.toLowerCase())}
                          className={`flex items-center gap-2.5 bg-white border rounded-full pl-1.5 pr-4 py-1.5 shadow-[0_1px_2px_rgba(16,24,40,.05)] transition-all ${on ? "border-[#0F62D6] shadow-[0_0_0_3px_#F3F7FE]" : "border-[#EAECF0] hover:border-[#CBDFF9]"}`}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-extrabold shrink-0 ${pet.needsRx ? "bg-[#FEF4E6] text-[#B54708]" : "bg-[#E6EFFC] text-[#0F62D6]"}`}>
                            {pet.name.charAt(0).toUpperCase()}
                          </span>
                          <span className="text-left">
                            <span className="block text-[13.5px] font-bold leading-tight capitalize">{pet.name}</span>
                            <span className="block text-[11px] text-[#667085] mt-0.5 capitalize">
                              {pet.species || "Pet"}{pet.needsRx ? " · needs Rx" : pet.active ? ` · ${pet.active} active` : ""}
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* notifications */}
              {notifications.length > 0 && (
                <div className="space-y-2.5 mb-5" data-testid="notifications-section">
                  {notifications.map(n => (
                    <div key={n.notification_id} className="bg-white border border-[#EAECF0] rounded-2xl px-5 py-4 flex items-center gap-3.5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                      <span className="w-[38px] h-[38px] rounded-[11px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center shrink-0"><Bell className="w-4 h-4" /></span>
                      <div className="min-w-0">
                        <div className="text-[14px] font-bold">{n.title}</div>
                        <div className="text-[12.5px] text-[#667085]">{n.message}</div>
                      </div>
                      <button onClick={() => markRead(n.notification_id)} data-testid={`dismiss-notification-${n.notification_id}`} className="ml-auto text-[12.5px] font-bold text-[#667085] hover:text-[#0F62D6] shrink-0">Dismiss</button>
                    </div>
                  ))}
                </div>
              )}

              {/* hero */}
              {heroOrder ? (
                <section className="rounded-[20px] overflow-hidden relative text-white shadow-[0_12px_32px_-8px_rgba(16,24,40,.18)]" style={{ background: "linear-gradient(120deg,#0B2E66 0%,#0F62D6 58%,#2C7BEA 100%)" }} data-testid="hero-order">
                  <span className="absolute -right-[70px] -top-[90px] w-[290px] h-[290px] rounded-full bg-white/[.07]" />
                  <span className="absolute right-[60px] -bottom-[120px] w-[210px] h-[210px] rounded-full bg-white/[.05]" />
                  <div className="relative z-[2] p-6 sm:p-7">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div>
                        <h2 className="text-[19px] sm:text-[21px] font-extrabold tracking-[-.02em]">
                          <span className="capitalize">{heroOrder.pet?.name ? `${heroOrder.pet.name}'s ` : ""}</span>{heroOrder.medication?.name} {heroOrder.status === "shipped" ? "is on the way" : ["delivered", "completed"].includes(heroOrder.status) ? "has been delivered" : "is in progress"}
                        </h2>
                        <div className="text-[13.5px] text-white/75 mt-1">
                          {heroOrder.order_number} · {money(heroOrder.payment?.amount)} {heroOrder.payment?.currency?.toUpperCase() || ""}
                        </div>
                      </div>
                      <span className="sm:ml-auto inline-flex items-center gap-2 bg-white/[.16] border border-white/25 rounded-full px-3.5 py-1.5 text-[12.5px] font-bold">
                        <i className="w-1.5 h-1.5 rounded-full bg-[#5EE9B5] not-italic" /> {STATUS_CONFIG[heroOrder.status]?.label || heroOrder.status}
                      </span>
                    </div>

                    <Tracker current={heroStep} />

                    <div className="flex items-center gap-3 flex-wrap mt-6 pt-5 border-t border-white/20">
                      <span className="text-[13px] text-white/75">
                        {heroOrder.refill_eligible && typeof heroOrder.rx_days_left === "number"
                          ? `Prescription valid · ${heroOrder.rx_days_left} days left`
                          : "We email you at every step."}
                      </span>
                      <div className="sm:ml-auto flex gap-2.5">
                        <Link to={`/order/${heroOrder.order_id}`} className="h-9 px-3.5 rounded-[9px] bg-white text-[#0F62D6] text-[13px] font-bold inline-flex items-center">Order details</Link>
                        {heroOrder.refill_eligible && (
                          <button onClick={(e) => handleRefill(e, heroOrder)} disabled={refilling === heroOrder.order_id} className="h-9 px-3.5 rounded-[9px] border border-white/30 text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60">
                            {refilling === heroOrder.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Refill
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </section>
              ) : heroEst ? (
                <section className="rounded-[20px] overflow-hidden relative text-white shadow-[0_12px_32px_-8px_rgba(16,24,40,.18)]" style={{ background: "linear-gradient(120deg,#0B2E66 0%,#0F62D6 58%,#2C7BEA 100%)" }} data-testid="hero-estimate">
                  <span className="absolute -right-[70px] -top-[90px] w-[290px] h-[290px] rounded-full bg-white/[.07]" />
                  <span className="absolute right-[60px] -bottom-[120px] w-[210px] h-[210px] rounded-full bg-white/[.05]" />
                  <div className="relative z-[2] p-6 sm:p-7">
                    <div className="flex items-center gap-4 flex-wrap">
                      <div>
                        <h2 className="text-[19px] sm:text-[21px] font-extrabold tracking-[-.02em]">
                          <span className="capitalize">{heroEst.pet_name ? `${heroEst.pet_name}'s ` : ""}</span>{heroEst.medication_name}
                          {heroEstCfg?.needsYou ? " needs you" : " is being checked"}
                        </h2>
                        <div className="text-[13.5px] text-white/75 mt-1">
                          Quoted {dateShort(heroEst.created_at)}
                          {heroEst.quote != null ? ` · ${money(heroEst.quote)}` : ""}
                        </div>
                      </div>
                      <span className="sm:ml-auto inline-flex items-center gap-2 bg-white/[.16] border border-white/25 rounded-full px-3.5 py-1.5 text-[12.5px] font-bold">
                        <i className={`w-1.5 h-1.5 rounded-full not-italic ${heroEstCfg?.needsYou ? "bg-[#FDB022]" : "bg-[#5EE9B5]"}`} /> {heroEstCfg?.label}
                      </span>
                    </div>

                    <Tracker current={EST_STEP[heroEst.status] ?? 1} />

                    <div className="flex items-center gap-3 flex-wrap mt-6 pt-5 border-t border-white/20">
                      <span className="text-[13px] text-white/75 max-w-[62ch]">{heroEstCfg?.hint}</span>
                      <Link to={`/estimate/${heroEst.link_token}`} className="sm:ml-auto h-9 px-3.5 rounded-[9px] bg-white text-[#0F62D6] text-[13px] font-bold inline-flex items-center gap-2 shrink-0">
                        {heroEstCfg?.cta} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    </div>
                  </div>
                </section>
              ) : (
                <section className="bg-white border border-[#EAECF0] rounded-2xl p-10 text-center shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                  <span className="w-14 h-14 rounded-2xl bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center mx-auto mb-4"><Package className="w-6 h-6" /></span>
                  <p className="text-[15px] font-bold">No active orders yet</p>
                  <p className="text-[13.5px] text-[#667085] mt-1.5 mb-5">Search for your pet&apos;s medication and we will price it against licensed pharmacies.</p>
                  <button onClick={openEstimate} data-testid="empty-new-order-btn" className="h-10 px-5 rounded-[10px] bg-[#0F62D6] hover:bg-[#0A4CAB] text-white text-[14px] font-bold inline-flex items-center gap-2">
                    Get your first estimate <ArrowRight className="w-4 h-4" />
                  </button>
                </section>
              )}

              {/* stats */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mt-5">
                <StatCard icon={Package} tint="bg-[#F3F7FE] text-[#0F62D6]" label="Active orders" value={activeOrders.length + liveEstimates.length}
                  sub={`${activeOrders.length} in progress · ${liveEstimates.length} quote${liveEstimates.length === 1 ? "" : "s"} open`} />
                <StatCard icon={Truck} tint="bg-[#E6F6F0] text-[#0E9F6E]" label="Shipping saved" value={money(rewards?.total_shipping_saved)}
                  sub="Refills ship at 50% off" valueClass="text-[#0E9F6E]" />
                <StatCard icon={Gift} tint="bg-[#F3F7FE] text-[#0F62D6]" label="Next order shipping"
                  value={rewards?.free_shipping_credit?.available ? "$0.00" : "Standard"}
                  sub={rewards?.free_shipping_credit?.available ? "Free delivery credit ready" : "No credit on your account"}
                  valueClass={rewards?.free_shipping_credit?.available ? "text-[#0F62D6]" : ""} />
                <StatCard icon={Heart} tint="bg-[#FEF4E6] text-[#B54708]" label="Donated for you" value={money(rewards?.total_donated)} sub="Pet surgery fund" />
              </div>

              {/* medications + savings */}
              <div className="grid grid-cols-1 xl:grid-cols-4 gap-4 mt-7 items-start">
                <div className="xl:col-span-3">
                  <div className="flex items-center gap-3 mb-3.5">
                    <h3 className="text-[16px] font-extrabold tracking-[-.015em] capitalize">{petFilter ? `${petFilter}'s medications` : "Your medications"}</h3>
                    {petFilter && <button onClick={() => setPetFilter(null)} className="text-[12.5px] font-bold text-[#667085] hover:text-[#0F62D6]">Show all</button>}
                    {orders.length > 0 && <button onClick={() => setTab("orders")} className="ml-auto text-[13px] font-bold text-[#0F62D6]">View all orders</button>}
                  </div>

                  {shownEstimates.map(est => <EstimateCard key={est.estimate_id} est={est} />)}
                  {shownOrders.map(order => <OrderCard key={order.order_id} order={order} />)}

                  {shownOrders.length + shownEstimates.length === 0 && (
                    <div className="bg-white border border-[#EAECF0] rounded-2xl p-8 text-center text-[13.5px] text-[#667085]">
                      {petFilter ? "Nothing in progress for this pet." : "Nothing in progress right now."}
                    </div>
                  )}
                </div>

                <aside>
                  <div className="flex items-center gap-3 mb-3.5">
                    <h3 className="text-[16px] font-extrabold tracking-[-.015em]">Your savings</h3>
                  </div>
                  <div className="space-y-4">
                  <div className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]" data-testid="rewards-card">
                    <h4 className="text-[14.5px] font-extrabold flex items-center gap-2.5">
                      <span className="w-7 h-7 rounded-lg bg-[#E6F6F0] text-[#0E9F6E] flex items-center justify-center"><Truck className="w-[15px] h-[15px]" /></span> Refill savings
                    </h4>
                    <div className="text-[32px] font-extrabold tracking-[-.035em] mt-3.5 leading-none text-[#0F62D6]">{money(rewards?.total_shipping_saved)}</div>
                    <p className="text-[12.5px] text-[#667085] mt-2 leading-relaxed">
                      Saved on shipping so far. Every refill ships at 50% off for as long as your pet&apos;s prescription stays valid. Your first order of each prescription ships at the standard rate.
                    </p>

                    {rewards?.free_shipping_credit?.available && (
                      <div className="flex gap-2.5 pt-3.5 mt-3.5 border-t border-[#F2F4F7]" data-testid="free-delivery-credit">
                        <span className="w-[30px] h-[30px] rounded-[9px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center shrink-0"><Gift className="w-[15px] h-[15px]" /></span>
                        <div>
                          <div className="text-[12.5px] font-bold">Free delivery on your next order</div>
                          <div className="text-[11.5px] text-[#667085] mt-0.5 leading-relaxed">A one-time credit sits on your account and applies automatically when your next quote is priced.</div>
                        </div>
                      </div>
                    )}

                    {(rewards?.held_prices || []).length > 0 && (
                      <div className="flex gap-2.5 pt-3.5 mt-3.5 border-t border-[#F2F4F7]" data-testid="held-price">
                        <span className="w-[30px] h-[30px] rounded-[9px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center shrink-0"><Lock className="w-[15px] h-[15px]" /></span>
                        <div>
                          <div className="text-[12.5px] font-bold">Your price is held</div>
                          {(rewards.held_prices || []).map((h, i) => (
                            <div key={i} className="text-[11.5px] text-[#667085] mt-0.5 leading-relaxed" data-testid={`held-price-row-${i}`}>
                              {[h.medication, h.strength, h.pack_label].filter(Boolean).join(" · ")}: you pay <b className="text-[#101828]">{money(h.your_price)}</b>
                              {h.list_price && h.list_price > h.your_price ? ` instead of the current ${money(h.list_price)}` : ""}.
                            </div>
                          ))}
                          <div className="text-[11.5px] text-[#667085] mt-1 leading-relaxed">It holds while our supplier cost holds, and we email you before it ever changes.</div>
                        </div>
                      </div>
                    )}

                    {rewards?.free_shipping_credit?.status === "redeemed" && (
                      <div className="text-[11.5px] text-[#667085] mt-3.5 pt-3.5 border-t border-[#F2F4F7]" data-testid="free-delivery-redeemed">
                        Your free-delivery credit saved you {money(rewards.free_shipping_credit.amount_saved)} on shipping.
                      </div>
                    )}
                  </div>

                  <div className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                    <h4 className="text-[14.5px] font-extrabold flex items-center gap-2.5">
                      <span className="w-7 h-7 rounded-lg bg-[#FEF4E6] text-[#B54708] flex items-center justify-center"><Heart className="w-[15px] h-[15px] fill-current" /></span> Giving back
                    </h4>
                    <p className="text-[12.5px] text-[#667085] mt-3 leading-relaxed">
                      PawLinx donates $5 for every medication ordered to help fund life-saving pet surgeries.
                      {rewards?.total_donated > 0 ? <> You have helped contribute <b className="text-[#101828]">{money(rewards.total_donated)}</b> so far.</> : null}
                    </p>
                  </div>

                  {testimonial?.unlocked && !testimonial?.submitted && (
                    <div className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                      <h4 className="text-[14.5px] font-extrabold flex items-center gap-2.5">
                        <span className="w-7 h-7 rounded-lg bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center"><Quote className="w-[15px] h-[15px]" /></span> Get a free delivery
                      </h4>
                      <p className="text-[12.5px] text-[#667085] mt-3 leading-relaxed">Share how PawLinx worked for your pet. One testimonial per account, and we add a free delivery credit to your next order.</p>
                      <button onClick={() => setTab("testimonial")} className="mt-3.5 w-full h-9 rounded-[9px] border border-[#EAECF0] text-[13px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6]">Write a testimonial</button>
                    </div>
                  )}
                  </div>
                </aside>
              </div>

              {/* order history */}
              {pastOrders.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-3.5">
                    <h3 className="text-[16px] font-extrabold tracking-[-.015em]">Order history</h3>
                  </div>
                  <HistoryTable rows={pastOrders.slice(0, 5)} />
                </div>
              )}
            </>
          )}

          {/* ============ ORDERS ============ */}
          {tab === "orders" && (
            <>
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em]">Orders</h1>
              <p className="text-[14px] text-[#667085] mt-1.5 mb-6">Everything you have ordered through PawLinx.</p>

              <div className="flex items-center gap-3 mb-3.5">
                <h3 className="text-[16px] font-extrabold tracking-[-.015em]">Active ({activeOrders.length + liveEstimates.length})</h3>
              </div>
              {pendingEstimates.map(est => <EstimateCard key={est.estimate_id} est={est} />)}
              {activeOrders.map(order => <OrderCard key={order.order_id} order={order} />)}
              {activeOrders.length + pendingEstimates.length === 0 && (
                <div className="bg-white border border-[#EAECF0] rounded-2xl p-8 text-center text-[13.5px] text-[#667085]">No active orders.</div>
              )}

              {pastOrders.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center gap-3 mb-3.5">
                    <h3 className="text-[16px] font-extrabold tracking-[-.015em]">History ({pastOrders.length})</h3>
                  </div>
                  <HistoryTable rows={pastOrders} />
                </div>
              )}
            </>
          )}

          {/* ============ PRESCRIPTIONS ============ */}
          {tab === "prescriptions" && (
            <div data-testid="prescriptions-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em]">Prescriptions</h1>
              <p className="text-[14px] text-[#667085] mt-1.5 mb-6">Every prescription we hold for your pets, and anything still waiting on your vet.</p>

              {pendingEstimates.length === 0 && orders.length === 0 ? (
                <div className="bg-white border border-[#EAECF0] rounded-2xl p-8 text-center text-[13.5px] text-[#667085]">No prescriptions on file yet.</div>
              ) : (
                <div className="space-y-3">
                  {pendingEstimates.map(est => {
                    const cfg = estCfg(est);
                    const dead = !!cfg.expired;
                    return (
                      <div key={est.estimate_id} className={`bg-white border rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)] ${dead ? "border-[#EAECF0]" : cfg.needsYou ? "border-[#F7DFC0]" : "border-[#EAECF0]"}`}>
                        <div className="flex gap-4 items-start">
                          <span className={`w-[46px] h-[46px] rounded-xl shrink-0 flex items-center justify-center border ${dead ? "bg-[#F9FAFB] border-[#EAECF0] text-[#98A2B3]" : cfg.needsYou ? "bg-[#FEF4E6] border-[#F7DFC0] text-[#B54708]" : "bg-[#F3F7FE] border-[#E6EFFC] text-[#0F62D6]"}`}>
                            <FileText className="w-[18px] h-[18px]" />
                          </span>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <h4 className="text-[15px] font-extrabold tracking-[-.012em] truncate">{est.medication_name}</h4>
                            <div className="text-[12.5px] text-[#667085] mt-1 capitalize">{est.pet_name || "Your pet"} · quoted {dateShort(est.created_at)}</div>
                          </div>
                          <div className="shrink-0 pt-0.5"><Badge tone={cfg.tone}>{cfg.label}</Badge></div>
                        </div>
                        <div className="mt-4 pt-4 border-t border-[#F2F4F7] flex items-center gap-3 flex-wrap">
                          <span className="text-[12.5px] text-[#667085]">{cfg.hint}</span>
                          {dead ? (
                            <button onClick={() => newQuoteFor(est.medication_name)} className="ml-auto shrink-0 h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6] inline-flex items-center gap-2">
                              {cfg.cta} <ArrowRight className="w-3.5 h-3.5" />
                            </button>
                          ) : (
                            <Link to={`/estimate/${est.link_token}`} className={`ml-auto shrink-0 h-9 px-3.5 rounded-[9px] text-[13px] font-bold inline-flex items-center gap-2 ${cfg.needsYou ? "bg-[#0F62D6] hover:bg-[#0A4CAB] text-white" : "border border-[#EAECF0] bg-white text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6]"}`}>
                              {cfg.cta} <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {orders.filter(o => o.refill_eligible || o.rx_exhausted).map(o => (
                    <div key={o.order_id} className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                      <div className="flex gap-4 items-start">
                        <span className="w-[46px] h-[46px] rounded-xl shrink-0 flex items-center justify-center bg-[#F3F7FE] border border-[#E6EFFC] text-[#0F62D6]"><Pill className="w-[18px] h-[18px]" /></span>
                        <div className="flex-1 min-w-0 pt-0.5">
                          <h4 className="text-[15px] font-extrabold tracking-[-.012em] truncate">{o.medication?.name}</h4>
                          <div className="text-[12.5px] text-[#667085] mt-1 capitalize">{o.pet?.name} · from order {o.order_number}</div>
                        </div>
                        <div className="shrink-0 pt-0.5">
                          <Badge tone={o.rx_exhausted ? "grey" : "green"}>{o.rx_exhausted ? "Used up" : "Valid"}</Badge>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-[#F2F4F7] flex items-center gap-3 flex-wrap">
                        <span className="text-[12.5px] text-[#667085]">
                          {o.rx_exhausted
                            ? "This prescription authorised a single fill. A new one from your vet is needed to order again."
                            : `No new upload needed${typeof o.rx_days_left === "number" ? ` · ${o.rx_days_left} days left` : ""}. Refills ship at 50% off.`}
                        </span>
                        {o.refill_eligible ? (
                          <button onClick={(e) => handleRefill(e, o)} disabled={refilling === o.order_id} className="ml-auto shrink-0 h-9 px-3.5 rounded-[9px] bg-[#0F62D6] hover:bg-[#0A4CAB] text-white text-[13px] font-bold inline-flex items-center gap-2 disabled:opacity-60">
                            {refilling === o.order_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Refill
                          </button>
                        ) : (
                          <button onClick={() => reorder(o)} className="ml-auto shrink-0 h-9 px-3.5 rounded-[9px] border border-[#EAECF0] bg-white text-[13px] font-bold text-[#475467] hover:border-[#CBDFF9] hover:text-[#0F62D6]">New estimate</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============ MY PETS ============ */}
          {tab === "pets" && (
            <div data-testid="pets-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em]">My pets</h1>
              <p className="text-[14px] text-[#667085] mt-1.5 mb-6">Built from what you have ordered. Every medication grouped by the pet it is for.</p>

              {pets.length === 0 ? (
                <div className="bg-white border border-[#EAECF0] rounded-2xl p-8 text-center">
                  <p className="text-[13.5px] text-[#667085] mb-4">No pets yet. They appear here as soon as you request your first estimate.</p>
                  <button onClick={openEstimate} className="h-10 px-5 rounded-[10px] bg-[#0F62D6] hover:bg-[#0A4CAB] text-white text-[14px] font-bold inline-flex items-center gap-2">Get an estimate <ArrowRight className="w-4 h-4" /></button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {pets.map(pet => (
                    <div key={pet.name} className="bg-white border border-[#EAECF0] rounded-2xl p-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                      <div className="flex items-center gap-3">
                        <span className={`w-11 h-11 rounded-full flex items-center justify-center text-[16px] font-extrabold shrink-0 ${pet.needsRx ? "bg-[#FEF4E6] text-[#B54708]" : "bg-[#E6EFFC] text-[#0F62D6]"}`}>{pet.name.charAt(0).toUpperCase()}</span>
                        <div className="min-w-0">
                          <h4 className="text-[16px] font-extrabold tracking-[-.015em] capitalize truncate">{pet.name}</h4>
                          <div className="text-[12.5px] text-[#667085] capitalize">{pet.species || "Pet"} · {pet.meds.length} medication{pet.meds.length === 1 ? "" : "s"}</div>
                        </div>
                        {pet.needsRx && <span className="ml-auto shrink-0"><Badge tone="amber">Needs Rx</Badge></span>}
                      </div>
                      <div className="mt-4 pt-4 border-t border-[#F2F4F7] space-y-2.5">
                        {pet.meds.map((m, i) => (
                          <Link key={i} to={m.to} className="flex items-center gap-3 group">
                            <span className="w-8 h-8 rounded-lg bg-[#F9FAFB] border border-[#EAECF0] flex items-center justify-center shrink-0 text-[#667085] group-hover:text-[#0F62D6] group-hover:border-[#CBDFF9]"><Pill className="w-4 h-4" /></span>
                            <span className="min-w-0">
                              <span className="block text-[13.5px] font-bold truncate">{m.label}</span>
                              <span className="block text-[12px] text-[#667085]">{m.sub}</span>
                            </span>
                            <ArrowRight className="w-4 h-4 ml-auto shrink-0 text-[#98A2B3] group-hover:text-[#0F62D6]" />
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ============ SAVINGS ============ */}
          {tab === "vetrefill" && (
            <div className="max-w-3xl" data-testid="vetrefill-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em]">Ask my vet for a refill</h1>
              <p className="text-[14px] text-[#667085] mt-1.5 mb-6">
                Out of refills on a prescription? We will contact your vet for you and ask them to authorize
                another one. You do not need to phone anyone or collect a piece of paper.
              </p>

              {!rfOpen ? (
                <button onClick={() => setRfOpen(true)} data-testid="vetrefill-open"
                  className="h-11 px-5 rounded-[10px] bg-[#0F62D6] hover:bg-[#0A4FB4] text-white text-[14px] font-bold inline-flex items-center gap-2 transition-colors">
                  <Stethoscope className="w-4 h-4" /> Request a refill through my vet
                </button>
              ) : (
                <form onSubmit={submitRefillRequest} data-testid="vetrefill-form"
                  className="bg-white border border-[#EAECF0] rounded-2xl p-5 sm:p-6 shadow-[0_1px_2px_rgba(16,24,40,.05)] space-y-4">
                  <div>
                    <Label className="text-[13px] font-bold">Which medication?</Label>
                    <select value={rf.order_id} onChange={pickRefillOrder} required data-testid="vetrefill-order"
                      className="mt-1.5 w-full h-11 rounded-[10px] border border-[#EAECF0] px-3 text-[14px] bg-white">
                      <option value="">Choose from your past orders</option>
                      {orders.map(o => (
                        <option key={o.order_id} value={o.order_id}>
                          {o.medication?.name}{o.pet?.name ? ` for ${o.pet.name}` : ""}
                        </option>
                      ))}
                    </select>
                    <p className="text-[12px] text-[#667085] mt-1.5">
                      Not in the list? Call us on 1-888-827-2717 and we will raise it for you.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-[13px] font-bold">Your vet or clinic name</Label>
                      <Input required value={rf.clinic_name} onChange={e => setRf({ ...rf, clinic_name: e.target.value })}
                        placeholder="Bright Paw Veterinary Clinic" className="mt-1.5 h-11 rounded-[10px]" data-testid="vetrefill-clinic-name" /></div>
                    <div><Label className="text-[13px] font-bold">Their email</Label>
                      <Input required type="email" value={rf.clinic_email} onChange={e => setRf({ ...rf, clinic_email: e.target.value })}
                        placeholder="reception@yourvet.com" className="mt-1.5 h-11 rounded-[10px]" data-testid="vetrefill-clinic-email" /></div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><Label className="text-[13px] font-bold">Their phone <span className="font-normal text-[#667085]">(optional)</span></Label>
                      <Input value={rf.clinic_phone} onChange={e => setRf({ ...rf, clinic_phone: e.target.value })}
                        placeholder="1-555-0142" className="mt-1.5 h-11 rounded-[10px]" /></div>
                    <div><Label className="text-[13px] font-bold">Anything we should mention <span className="font-normal text-[#667085]">(optional)</span></Label>
                      <Input value={rf.note} onChange={e => setRf({ ...rf, note: e.target.value })}
                        placeholder="For example, running low this week" className="mt-1.5 h-11 rounded-[10px]" /></div>
                  </div>

                  <div className="rounded-[10px] bg-[#F3F7FE] border border-[#E6EFFC] px-4 py-3">
                    <p className="text-[12.5px] text-[#475467] leading-relaxed">
                      A member of the PawLinx team checks your request, then emails your clinic a secure form
                      with your details already filled in. They authorize or decline, and we email you either
                      way. If they do not reply within three days we call them.
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button type="submit" disabled={rfSaving} data-testid="vetrefill-submit"
                      className="h-11 px-5 rounded-[10px] bg-[#0F62D6] hover:bg-[#0A4FB4] disabled:opacity-60 text-white text-[14px] font-bold inline-flex items-center gap-2 transition-colors">
                      {rfSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send the request</>}
                    </button>
                    <button type="button" onClick={() => setRfOpen(false)}
                      className="h-11 px-5 rounded-[10px] border border-[#EAECF0] text-[14px] font-bold text-[#475467] hover:border-[#CBDFF9]">Cancel</button>
                  </div>
                </form>
              )}

              <h2 className="text-[15px] font-extrabold mt-8 mb-3">Requests you have made</h2>
              {refillReqs.length === 0 ? (
                <div className="bg-white border border-[#EAECF0] rounded-2xl p-8 text-center text-[13.5px] text-[#667085]">
                  Nothing yet.
                </div>
              ) : (
                <div className="space-y-3" data-testid="vetrefill-list">
                  {refillReqs.map(q => {
                    const cfg = RF_STATUS[q.status] || RF_STATUS.emailed;
                    return (
                      <div key={q.request_id} className="bg-white border border-[#EAECF0] rounded-2xl p-5">
                        <div className="flex items-start gap-3">
                          <span className={`w-[42px] h-[42px] rounded-xl shrink-0 flex items-center justify-center border ${cfg.tint}`}>
                            <Stethoscope className="w-[17px] h-[17px]" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="text-[15px] font-extrabold truncate">{q.medication}</div>
                            <div className="text-[12.5px] text-[#667085] mt-0.5 capitalize">
                              {q.pet_name || "Your pet"} &middot; {q.clinic_name} &middot; sent {dateShort(q.created_at)}
                            </div>
                            <div className={`inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full text-[12px] font-bold ${cfg.pill}`}>
                              {cfg.label}
                            </div>
                            {q.decline_reason ? (
                              <p className="text-[12.5px] text-[#667085] mt-2 leading-relaxed">
                                Your vet said: {q.decline_reason}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {tab === "savings" && (
            <div className="max-w-3xl" data-testid="savings-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em]">Savings</h1>
              <p className="text-[14px] text-[#667085] mt-1.5 mb-6">What PawLinx has saved you, and what is sitting on your account right now.</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
                <StatCard icon={Truck} tint="bg-[#E6F6F0] text-[#0E9F6E]" label="Shipping saved" value={money(rewards?.total_shipping_saved)} sub="Across every refill" valueClass="text-[#0E9F6E]" />
                <StatCard icon={Gift} tint="bg-[#F3F7FE] text-[#0F62D6]" label="Next order shipping" value={rewards?.free_shipping_credit?.available ? "$0.00" : "Standard"} sub={rewards?.free_shipping_credit?.available ? "Credit ready" : "No credit yet"} valueClass={rewards?.free_shipping_credit?.available ? "text-[#0F62D6]" : ""} />
                <StatCard icon={Heart} tint="bg-[#FEF4E6] text-[#B54708]" label="Donated for you" value={money(rewards?.total_donated)} sub="Pet surgery fund" />
              </div>

              <div className="bg-white border border-[#EAECF0] rounded-2xl p-6 shadow-[0_1px_2px_rgba(16,24,40,.05)] space-y-5">
                <div className="flex gap-3">
                  <span className="w-9 h-9 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center shrink-0"><Truck className="w-[18px] h-[18px]" /></span>
                  <div>
                    <div className="text-[14.5px] font-extrabold">Every refill ships at 50% off</div>
                    <p className="text-[13px] text-[#667085] mt-1 leading-relaxed">For as long as your pet&apos;s prescription stays valid. Your first order of each prescription ships at the standard rate.</p>
                  </div>
                </div>

                {rewards?.free_shipping_credit?.available && (
                  <div className="flex gap-3 pt-5 border-t border-[#F2F4F7]">
                    <span className="w-9 h-9 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center shrink-0"><Gift className="w-[18px] h-[18px]" /></span>
                    <div>
                      <div className="text-[14.5px] font-extrabold">Free delivery on your next order</div>
                      <p className="text-[13px] text-[#667085] mt-1 leading-relaxed">A one-time credit is on your account. Shipping will be $0.00 next time, applied automatically when your quote is priced.</p>
                    </div>
                  </div>
                )}

                {(rewards?.held_prices || []).length > 0 && (
                  <div className="flex gap-3 pt-5 border-t border-[#F2F4F7]">
                    <span className="w-9 h-9 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center shrink-0"><Lock className="w-[18px] h-[18px]" /></span>
                    <div>
                      <div className="text-[14.5px] font-extrabold">Your price is held</div>
                      {(rewards.held_prices || []).map((h, i) => (
                        <p key={i} className="text-[13px] text-[#667085] mt-1 leading-relaxed">
                          {[h.medication, h.strength, h.pack_label].filter(Boolean).join(" · ")}: you pay <b className="text-[#101828]">{money(h.your_price)}</b>
                          {h.list_price && h.list_price > h.your_price ? ` instead of the current ${money(h.list_price)}` : ""}.
                        </p>
                      ))}
                      <p className="text-[13px] text-[#667085] mt-1 leading-relaxed">It holds while our supplier cost holds, and we email you before it ever changes.</p>
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-5 border-t border-[#F2F4F7]">
                  <span className="w-9 h-9 rounded-[10px] bg-[#FEF4E6] text-[#B54708] flex items-center justify-center shrink-0"><Heart className="w-[18px] h-[18px] fill-current" /></span>
                  <div>
                    <div className="text-[14.5px] font-extrabold">Giving back</div>
                    <p className="text-[13px] text-[#667085] mt-1 leading-relaxed">
                      PawLinx donates $5 for every medication ordered to help fund life-saving pet surgeries.
                      {rewards?.total_donated > 0 ? <> You have helped contribute <b className="text-[#101828]">{money(rewards.total_donated)}</b> so far.</> : null}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ============ PROFILE ============ */}
          {tab === "profile" && (
            <form onSubmit={handleProfileSave} className="max-w-2xl" data-testid="profile-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em] mb-6">Profile &amp; address</h1>

              <div className="bg-white rounded-2xl border border-[#EAECF0] p-6 md:p-7 mb-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                <h2 className="text-[16px] font-extrabold flex items-center gap-2.5 mb-5">
                  <span className="w-8 h-8 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center"><User className="w-4 h-4" /></span> Personal details
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">First name</Label>
                      <Input value={profile.first_name} onChange={(e) => setProfile({ ...profile, first_name: e.target.value })} className="rounded-xl border-[#EAECF0]" data-testid="profile-first-name" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Last name</Label>
                      <Input value={profile.last_name} onChange={(e) => setProfile({ ...profile, last_name: e.target.value })} className="rounded-xl border-[#EAECF0]" data-testid="profile-last-name" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Email</Label>
                    <Input value={user?.email || ""} disabled className="rounded-xl border-[#EAECF0] bg-[#F7F8FA] cursor-not-allowed" />
                    <p className="text-xs text-[#667085]">Email cannot be changed.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Phone number</Label>
                      <Input type="tel" value={profile.phone} onChange={(e) => setProfile({ ...profile, phone: e.target.value })} placeholder="+1 (555) 123-4567" className="rounded-xl border-[#EAECF0]" data-testid="profile-phone" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Date of birth</Label>
                      <Input type="date" value={profile.dob} onChange={(e) => setProfile({ ...profile, dob: e.target.value })} className="rounded-xl border-[#EAECF0]" data-testid="profile-dob" />
                      <p className="text-xs text-[#667085]">Saved once so you never have to re-enter it, and used to verify you on the phone line.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-[#EAECF0] p-6 md:p-7 mb-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                <h2 className="text-[16px] font-extrabold flex items-center gap-2.5 mb-2">
                  <span className="w-8 h-8 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center"><MapPin className="w-4 h-4" /></span> Shipping address
                </h2>
                <p className="text-sm text-[#667085] mb-5">Start typing and pick your address from the list so deliveries reach you reliably.</p>
                <div className="space-y-2">
                  <Label className="text-sm font-semibold">Address</Label>
                  <AddressAutocomplete
                    value={profile.address}
                    onChange={(text) => setProfile({ ...profile, address: text })}
                    onSelect={(parts) => { setAddressParts(parts); setProfile((p) => ({ ...p, address: parts.formatted || p.address })); }}
                    placeholder="Start typing your address"
                  />
                  {addressParts?.city && (
                    <p className="text-xs text-[#667085] pt-1">
                      {[addressParts.city, addressParts.state_province, addressParts.postal_code].filter(Boolean).join(", ")}
                    </p>
                  )}
                </div>
              </div>

              <Button type="submit" disabled={savingProfile} className="rounded-[10px] h-10 bg-[#0F62D6] hover:bg-[#0A4CAB] text-white font-bold" data-testid="save-profile-btn">
                {savingProfile ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save changes
              </Button>
            </form>
          )}

          {/* ============ TESTIMONIAL ============ */}
          {tab === "testimonial" && (
            <div className="max-w-2xl" data-testid="testimonial-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em] mb-6">Testimonial</h1>
              {!testimonial ? (
                <div className="bg-white rounded-2xl border border-[#EAECF0] p-7 flex items-center gap-3" data-testid="testimonial-loading">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0F62D6]" />
                  <span className="text-sm text-[#667085]">Loading your testimonial…</span>
                </div>
              ) : !testimonial.unlocked ? (
                <div className="bg-white rounded-2xl border border-[#EAECF0] p-6 md:p-7" data-testid="testimonial-locked">
                  <h2 className="text-[16px] font-extrabold flex items-center gap-2.5 mb-3">
                    <span className="w-8 h-8 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center"><Lock className="w-4 h-4" /></span> Unlocks after your first delivery
                  </h2>
                  <p className="text-sm text-[#667085] mb-5 leading-relaxed">
                    As soon as your first order has been delivered, you can share one testimonial about your experience here, and we will add a <strong className="text-[#101828]">free delivery</strong> to your account to say thank you.
                  </p>
                  <div className="rounded-xl bg-[#F3F7FE] border border-[#E6EFFC] p-4 flex items-start gap-3">
                    <Gift className="w-5 h-5 text-[#0F62D6] shrink-0 mt-0.5" />
                    <p className="text-sm text-[#667085]">One testimonial per account, one free delivery. It is applied automatically to your next order, and there is nothing to enter at checkout.</p>
                  </div>
                </div>
              ) : testimonial.submitted ? (
                <div className="bg-white rounded-2xl border border-[#EAECF0] p-6 md:p-7" data-testid="testimonial-submitted">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <h2 className="text-[16px] font-extrabold flex items-center gap-2.5">
                      <CheckCircle2 className="w-5 h-5 text-[#0E9F6E]" /> Thank you for your testimonial
                    </h2>
                    <span data-testid="testimonial-status"><Badge tone="green" dot={false}>
                      {testimonial.testimonial?.status === "approved" ? "Published" : testimonial.testimonial?.status === "rejected" ? "Not published" : "Received"}
                    </Badge></span>
                  </div>
                  <div className="flex items-center gap-1 mb-3" data-testid="testimonial-stars-readonly" aria-label={`${testimonial.testimonial?.rating || 5} out of 5`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= (testimonial.testimonial?.rating || 5) ? "text-[#0F62D6] fill-[#0F62D6]" : "text-[#98A2B3]"}`} aria-hidden="true" />
                    ))}
                  </div>
                  <blockquote className="text-sm leading-relaxed border-l-2 border-[#CBDFF9] pl-4 mb-4 whitespace-pre-wrap" data-testid="testimonial-body-readonly">
                    {testimonial.testimonial?.body}
                  </blockquote>
                  {testimonial.testimonial?.pet_name && (
                    <p className="text-xs text-[#667085] mb-5" data-testid="testimonial-pet">About {testimonial.testimonial.pet_name}</p>
                  )}
                  <div className="rounded-xl bg-[#F3F7FE] border border-[#E6EFFC] p-4 flex items-start gap-3" data-testid="testimonial-reward">
                    {testimonial.reward?.queued ? <Hourglass className="w-5 h-5 text-[#0F62D6] shrink-0 mt-0.5" /> : <Gift className="w-5 h-5 text-[#0F62D6] shrink-0 mt-0.5" />}
                    <p className="text-sm text-[#667085]">
                      {testimonial.reward?.queued
                        ? "Your free delivery is queued. It applies automatically once the credit already on your account has been used. You keep both."
                        : "Your free delivery is on your account and will be applied automatically to your next order."}
                    </p>
                  </div>
                </div>
              ) : (
                <form onSubmit={submitTestimonial} className="bg-white rounded-2xl border border-[#EAECF0] p-6 md:p-7" data-testid="testimonial-form">
                  <h2 className="text-[16px] font-extrabold flex items-center gap-2.5 mb-2">
                    <span className="w-8 h-8 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center"><Quote className="w-4 h-4" /></span> Share your experience
                  </h2>
                  <p className="text-sm text-[#667085] mb-6 leading-relaxed">
                    Your first order has been delivered, so this is unlocked. Tell other pet owners how it went, and we will add a <strong className="text-[#101828]">free delivery</strong> to your account as a thank-you. You can write one testimonial, once, so take your time with it.
                  </p>

                  <div className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">How would you rate us?</Label>
                      <div className="flex items-center gap-1.5" data-testid="testimonial-rating">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <button key={n} type="button" onClick={() => setTRating(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} aria-pressed={tRating === n} data-testid={`testimonial-star-${n}`} className="p-0.5 rounded focus:outline-none focus:ring-2 focus:ring-[#0F62D6]">
                            <Star className={`w-6 h-6 transition-colors ${n <= tRating ? "text-[#0F62D6] fill-[#0F62D6]" : "text-[#98A2B3]"}`} aria-hidden="true" />
                          </button>
                        ))}
                        <span className="text-sm text-[#667085] ml-2">{tRating} of 5</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Your pet&apos;s name <span className="text-[#667085] font-normal">(optional)</span></Label>
                      <Input value={tPet} onChange={(e) => setTPet(e.target.value)} maxLength={60} placeholder="e.g. Eevee" className="rounded-xl border-[#EAECF0]" data-testid="testimonial-pet-name" />
                    </div>

                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Your testimonial</Label>
                      <textarea
                        rows={6}
                        value={tBody}
                        onChange={(e) => setTBody(e.target.value.slice(0, testimonial.max_chars || 1500))}
                        placeholder="What was it like ordering with us? How did the delivery go? Anything that would help another pet owner decide."
                        className="w-full rounded-xl border border-[#EAECF0] p-3 text-sm focus:outline-none focus:border-[#0F62D6] resize-none"
                        data-testid="testimonial-body"
                      />
                      <p className="text-xs text-[#667085]" data-testid="testimonial-counter">
                        {tBody.trim().length < (testimonial.min_chars || 40)
                          ? `${(testimonial.min_chars || 40) - tBody.trim().length} more characters needed`
                          : `${tBody.trim().length} of ${testimonial.max_chars || 1500} characters`}
                      </p>
                    </div>

                    <label className="flex items-start gap-3 cursor-pointer" data-testid="testimonial-consent-row">
                      <input type="checkbox" checked={tConsent} onChange={(e) => setTConsent(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#0F62D6] shrink-0" data-testid="testimonial-consent" />
                      <span className="text-sm text-[#667085]">
                        You may publish this on the PawLinx website with my first name and my pet&apos;s name. Leave this unticked and we will keep it internal. Your free delivery is the same either way.
                      </span>
                    </label>

                    <div className="rounded-xl bg-[#F3F7FE] border border-[#E6EFFC] p-4 flex items-start gap-3">
                      <Gift className="w-5 h-5 text-[#0F62D6] shrink-0 mt-0.5" />
                      <p className="text-sm text-[#667085]">
                        Submitting adds one free delivery to your account straight away. If you are already holding a free delivery, this one waits behind it. You keep both.
                      </p>
                    </div>

                    <Button type="submit" disabled={tSubmitting || tBody.trim().length < (testimonial.min_chars || 40)} className="rounded-[10px] h-10 bg-[#0F62D6] hover:bg-[#0A4CAB] text-white font-bold" data-testid="testimonial-submit">
                      {tSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Quote className="w-4 h-4 mr-2" />}
                      Submit testimonial
                    </Button>
                  </div>
                </form>
              )}
            </div>
          )}

          {/* ============ SECURITY ============ */}
          {tab === "security" && (
            <div className="max-w-2xl" data-testid="security-tab">
              <h1 className="text-[24px] sm:text-[26px] font-extrabold tracking-[-.025em] mb-6">Security</h1>

              <form onSubmit={handlePasswordChange} className="bg-white rounded-2xl border border-[#EAECF0] p-6 md:p-7 mb-5 shadow-[0_1px_2px_rgba(16,24,40,.05)]">
                <h2 className="text-[16px] font-extrabold flex items-center gap-2.5 mb-5">
                  <span className="w-8 h-8 rounded-[10px] bg-[#F3F7FE] text-[#0F62D6] flex items-center justify-center"><Lock className="w-4 h-4" /></span> Change password
                </h2>
                <div className="space-y-4">
                  {!user?.is_guest && (
                    <div className="space-y-2">
                      <Label className="text-sm font-semibold">Current password</Label>
                      <Input type="password" value={passwords.current_password} onChange={(e) => setPasswords({ ...passwords, current_password: e.target.value })} className="rounded-xl border-[#EAECF0]" data-testid="current-password" />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">New password</Label>
                    <Input type="password" value={passwords.new_password} onChange={(e) => setPasswords({ ...passwords, new_password: e.target.value })} placeholder="Min 6 characters" className="rounded-xl border-[#EAECF0]" data-testid="new-password" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Confirm new password</Label>
                    <Input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({ ...passwords, confirm_password: e.target.value })} className="rounded-xl border-[#EAECF0]" data-testid="confirm-password" />
                  </div>
                  <Button type="submit" disabled={savingPassword} className="rounded-[10px] h-10 bg-[#0F62D6] hover:bg-[#0A4CAB] text-white font-bold" data-testid="change-password-btn">
                    {savingPassword ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                    {user?.is_guest ? "Set password" : "Change password"}
                  </Button>
                </div>
              </form>

              <div className="bg-white rounded-2xl border border-[#FECDC9] p-6 md:p-7" data-testid="danger-zone">
                <h2 className="text-[16px] font-extrabold text-[#B42318] flex items-center gap-2.5 mb-3">
                  <Trash2 className="w-5 h-5" /> Delete account
                </h2>
                <p className="text-sm text-[#667085] mb-5 leading-relaxed">
                  This deactivates your account and schedules it for deletion. Your information is kept for <strong className="text-[#101828]">30 days</strong>, then permanently removed. You can cancel any time within those 30 days by logging back in.
                </p>
                <Button type="button" onClick={handleDeleteAccount} disabled={deleting} className="rounded-[10px] h-10 bg-[#B42318] hover:bg-[#912018] text-white font-bold" data-testid="delete-account-btn">
                  {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                  Delete my account
                </Button>
              </div>
            </div>
          )}

          <div className="mt-10 pt-6 border-t border-[#EAECF0] flex flex-wrap gap-6 text-[13.5px] text-[#667085]">
            <Link to="/contact" className="hover:text-[#0F62D6] inline-flex items-center gap-2"><LifeBuoy className="w-4 h-4" /> Contact support</Link>
            <Link to="/shipping-refund" className="hover:text-[#0F62D6]">Shipping &amp; refunds</Link>
            <Link to="/privacy" className="hover:text-[#0F62D6]">Privacy</Link>
            {!readOnly && <button onClick={() => { logout(); navigate("/"); }} className="hover:text-[#0F62D6] lg:hidden">Log out</button>}
          </div>
        </div>
      </div>

      {/* Refund request modal */}
      {refundOrder && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="refund-modal" onClick={() => !refundSubmitting && setRefundOrder(null)}>
          <div className="bg-white rounded-2xl border border-[#EAECF0] p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="text-[17px] font-extrabold flex items-center gap-2"><Undo2 className="w-5 h-5 text-[#B42318]" /> Request a refund</h3>
              <button onClick={() => setRefundOrder(null)} className="text-[#667085] hover:text-[#101828]" aria-label="Close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#667085] mb-4 leading-relaxed">
              Order <span className="font-bold text-[#101828]">{refundOrder.order_number}</span> · {refundOrder.medication?.name}. Tell us why you&apos;d like a refund. Our team reviews every request and will email you a decision. No money moves automatically.
            </p>
            <textarea
              rows={4}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Reason for your refund request…"
              className="w-full rounded-xl border border-[#EAECF0] p-3 text-sm focus:outline-none focus:border-[#0F62D6] resize-none"
              data-testid="refund-reason"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setRefundOrder(null)} disabled={refundSubmitting} className="rounded-[10px] px-4 py-2 text-sm font-bold text-[#667085] hover:text-[#101828] disabled:opacity-60">Cancel</button>
              <button onClick={submitRefund} disabled={refundSubmitting} className="inline-flex items-center gap-2 rounded-[10px] bg-[#B42318] hover:bg-[#912018] text-white px-4 py-2 text-sm font-bold disabled:opacity-60" data-testid="refund-submit">
                {refundSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />} Submit request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

}
